import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './SystemDashboard.css';
import { useAuth } from '../../hooks/useAuth';
import { ArrowLeft, LogOut, LayoutDashboard, Users, MessageSquare, Stethoscope, Globe } from 'lucide-react';
import OverviewTab from './components/OverviewTab';
import UsersTab from './components/UsersTab';
import SocialTab from './components/SocialTab';
import MedicalTab from './components/MedicalTab';
import DashboardFilters from './components/DashboardFilters';
import { DEFAULT_FILTERS } from './constants/filters';

import DetailedOverviewPage from './pages/DetailedOverviewPage';
import DetailedUsersPage from './pages/DetailedUsersPage';
import DetailedSocialPage from './pages/DetailedSocialPage';
import DetailedMedicalPage from './pages/DetailedMedicalPage';

const SystemDashboard = () => {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/auth/login');
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const activeTab = searchParams.get('tab') || 'overview';
    const viewMode = searchParams.get('view') || 'summary';

    const filters = {
        timeRange: searchParams.get('timeRange') || DEFAULT_FILTERS.timeRange,
        category: searchParams.get('category') || DEFAULT_FILTERS.category,
        start_date: searchParams.get('start_date') || DEFAULT_FILTERS.start_date,
        end_date: searchParams.get('end_date') || DEFAULT_FILTERS.end_date,
        gender: searchParams.get('gender') || DEFAULT_FILTERS.gender,
        min_age: searchParams.get('min_age') || DEFAULT_FILTERS.min_age,
        max_age: searchParams.get('max_age') || DEFAULT_FILTERS.max_age,
        min_reactions: searchParams.get('min_reactions') || DEFAULT_FILTERS.min_reactions,
        min_comments: searchParams.get('min_comments') || DEFAULT_FILTERS.min_comments,
        visit_type: searchParams.get('visit_type') || DEFAULT_FILTERS.visit_type,
        country_id: searchParams.get('country_id') || DEFAULT_FILTERS.country_id
    };

    const categoryConfig = {
        users: {
            show: true,
            label: t('systemDashboard.common.type'),
            options: [
                { value: 'PET_GUARDIAN', label: t('adminDashboard.userTypes.petGuardian') },
                { value: 'VETERINARIAN', label: t('adminDashboard.userTypes.veterinarian') },
                { value: 'SERVICE_PROVIDER', label: t('adminDashboard.userTypes.serviceProvider') },
                { value: 'FIRM', label: t('adminDashboard.userTypes.firm') }
            ]
        },
        social: {
            show: true,
            label: t('systemDashboard.common.type'),
            options: [
                { value: 'post', label: t('systemDashboard.social.post') },
                { value: 'poll', label: t('systemDashboard.social.poll') },
                { value: 'adoption', label: t('systemDashboard.social.adoption') }
            ]
        },
        medical: {
            show: true,
            label: t('systemDashboard.medical.status'),
            options: [
                { value: 'pending', label: t('systemDashboard.medical.statusLabels.pending') },
                { value: 'confirmed', label: t('systemDashboard.medical.statusLabels.confirmed') },
                { value: 'completed', label: t('systemDashboard.medical.statusLabels.completed') },
                { value: 'cancelled', label: t('systemDashboard.medical.statusLabels.cancelled') }
            ]
        },
        overview: {
            show: false
        }
    };

    const tabs = [
        { id: 'overview', label: t('systemDashboard.tabs.overview'), icon: LayoutDashboard },
        { id: 'users', label: t('systemDashboard.tabs.users'), icon: Users },
        { id: 'social', label: t('systemDashboard.tabs.social'), icon: MessageSquare },
        { id: 'medical', label: t('systemDashboard.tabs.medical'), icon: Stethoscope }
    ];

    const handleFilterChange = (newFilters) => {
        const newParams = new URLSearchParams(searchParams);
        Object.entries(newFilters).forEach(([key, value]) => {
            // Keep 'all' for timeRange (it is a distinct state from the default 30days),
            // but treat category='all' as the default and remove it from the URL.
            const shouldKeep = value && (key !== 'category' || value !== 'all');
            if (shouldKeep) {
                newParams.set(key, value);
            } else {
                newParams.delete(key);
            }
        });
        setSearchParams(newParams);
    };

    const setActiveTab = (tab) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('tab', tab);
        newParams.set('view', 'summary');
        newParams.set('category', 'all');

        ['gender', 'min_age', 'max_age', 'country_id', 'min_reactions', 'min_comments', 'visit_type'].forEach(
            (key) => newParams.delete(key)
        );

        setSearchParams(newParams);
    };

    const setViewMode = (view) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('view', view);
        setSearchParams(newParams);
    };

    const ActiveIcon = tabs.find((t) => t.id === activeTab)?.icon || LayoutDashboard;

    return (
        <div className="dashboard-container">
            <div className="dashboard-content">
                {/* Top nav — fixed LTR layout so buttons don't mirror in Arabic */}
                <div className="dashboard-nav-bar" dir="ltr">
                    <div className="dashboard-nav-group">
                        <button
                            className="dashboard-nav-btn dashboard-nav-btn--back"
                            onClick={() => navigate('/admin/dashboard')}
                            title={t('adminDashboard.title')}
                        >
                            <ArrowLeft size={16} />
                            <span>{t('adminDashboard.title')}</span>
                        </button>
                        <button
                            className="dashboard-nav-btn dashboard-nav-btn--language"
                            onClick={() => {
                                const newLang = i18n.language === 'en' ? 'ar' : 'en';
                                i18n.changeLanguage(newLang);
                            }}
                            title={i18n.language === 'en' ? t('language.switchToArabic') : t('language.switchToEnglish')}
                        >
                            <Globe size={16} />
                            <span>{i18n.language === 'en' ? 'AR' : 'EN'}</span>
                        </button>
                    </div>
                    <button
                        className="dashboard-nav-btn dashboard-nav-btn--logout"
                        onClick={handleLogout}
                        title={t('adminDashboard.logout')}
                    >
                        <LogOut size={16} />
                        <span>{t('adminDashboard.logout')}</span>
                    </button>
                </div>

                {/* Header */}
                {viewMode === 'summary' ? (
                    <div className="dashboard-header sd-header">
                        <h1 className="header-title">{t('systemDashboard.header.title')}</h1>
                        <p className="header-subtitle">{t('systemDashboard.header.subtitle')}</p>
                    </div>
                ) : (
                    <div className="dashboard-header-mini sd-header">
                        <div className="sd-detailed-header__left">
                            <button
                                onClick={() => setViewMode('summary')}
                                className="sd-detailed-header__back"
                                title={t('systemDashboard.detailed.backToDashboard')}
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <ActiveIcon size={22} className="sd-header__icon" />
                            <div>
                                <h1 className="sd-detailed-header__title">
                                    {t(`systemDashboard.detailed.titles.${activeTab}`)}
                                </h1>
                                <p className="sd-detailed-header__subtitle">{t('systemDashboard.detailed.subtitle')}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tabs + Filters row */}
                {viewMode === 'summary' && (
                    <div className="dashboard-toolbar">
                        <div className="tabs-section">
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                return (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                                    >
                                        <Icon size={16} />
                                        <span>{tab.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <DashboardFilters
                            currentFilters={filters}
                            onFilterChange={handleFilterChange}
                            showCategoryFilter={categoryConfig[activeTab]?.show}
                            categoryLabel={categoryConfig[activeTab]?.label}
                            categories={categoryConfig[activeTab]?.options}
                            activeTab={activeTab}
                        />
                    </div>
                )}

                {viewMode === 'detailed' && (
                    <div className="sd-detailed-filters">
                        <DashboardFilters
                            currentFilters={filters}
                            onFilterChange={handleFilterChange}
                            showCategoryFilter={categoryConfig[activeTab]?.show}
                            categoryLabel={categoryConfig[activeTab]?.label}
                            categories={categoryConfig[activeTab]?.options}
                            activeTab={activeTab}
                        />
                    </div>
                )}

                {/* Content Area */}
                <div className="tab-content">
                    {viewMode === 'summary' ? (
                        <>
                            {activeTab === 'overview' && (
                                <OverviewTab
                                    filters={filters}
                                    onViewDetailed={() => setViewMode('detailed')}
                                />
                            )}
                            {activeTab === 'users' && (
                                <UsersTab
                                    filters={filters}
                                    onViewDetailed={() => setViewMode('detailed')}
                                />
                            )}
                            {activeTab === 'social' && (
                                <SocialTab
                                    filters={filters}
                                    onViewDetailed={() => setViewMode('detailed')}
                                />
                            )}
                            {activeTab === 'medical' && (
                                <MedicalTab
                                    filters={filters}
                                    onViewDetailed={() => setViewMode('detailed')}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            {activeTab === 'overview' && (
                                <DetailedOverviewPage
                                    isSubView={true}
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                />
                            )}
                            {activeTab === 'users' && (
                                <DetailedUsersPage
                                    isSubView={true}
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                />
                            )}
                            {activeTab === 'social' && (
                                <DetailedSocialPage
                                    isSubView={true}
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                />
                            )}
                            {activeTab === 'medical' && (
                                <DetailedMedicalPage
                                    isSubView={true}
                                    filters={filters}
                                    onFilterChange={handleFilterChange}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SystemDashboard;
